import React, { useEffect } from "react"
import PropTypes from "prop-types"
import { Form, InputNumber, Space } from "antd"
import { useTranslation } from "react-i18next"
import styles from "./BookingForm.module.css"

const ALL_GUEST_FIELDS = [
  { key: "adults", labelKey: "booking.labels.adults", allowedFlag: "adults_allowed" },
  { key: "children", labelKey: "booking.labels.children", allowedFlag: "children_allowed" },
  { key: "infants", labelKey: "booking.labels.infants", allowedFlag: "infants_allowed" },
  { key: "passengers", labelKey: "booking.labels.passengers", allowedFlag: "passengers_allowed" }
]

const GuestCountSection = ({ form, tripPrice, onChange, minGuests, maxGuests, bookingSelections }) => {
  const { t } = useTranslation()

  // First guest type that is not explicitly disabled — receives the min-guest default.
  const firstAllowedField =
    ALL_GUEST_FIELDS.find(({ allowedFlag }) => !tripPrice || tripPrice[allowedFlag] !== false)?.key ?? "adults"
  const defaultMinCount = minGuests && minGuests > 0 ? minGuests : 1

  // Whether the user (or the saved booking) already has a guest count. Navigating to step 2 and back
  // remounts this component; without this guard the effects below would re-seed the minimum over the
  // existing selection (e.g. picking the previous trip's higher minimum), so anchor to the persisted
  // selection instead of the transient form field.
  const savedMain = bookingSelections?.counts?.main
  const hasSelection = !!savedMain && Object.values(savedMain).some((v) => Number(v) > 0)

  useEffect(() => {
    // On mount (including a step-2 → step-1 remount), restore the persisted counts so the saved/
    // selected values are shown rather than the seeded minimum.
    if (hasSelection) {
      form.setFieldsValue({ main: savedMain })
      onChange?.(savedMain)
    } else {
      onChange?.(form.getFieldValue("main") || {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Sync the min default once minGuests / tripPrice resolves — but never over an existing selection.
  useEffect(() => {
    if (hasSelection) return
    const current = form.getFieldValue(["main", firstAllowedField])
    if (current == null || current < defaultMinCount) {
      form.setFieldValue(["main", firstAllowedField], defaultMinCount)
      handleChange()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultMinCount, firstAllowedField])

  useEffect(() => {
    if (!tripPrice) return
    const updates = {}
    ALL_GUEST_FIELDS.forEach(({ key, allowedFlag }) => {
      if (key !== firstAllowedField && tripPrice[allowedFlag] === false) {
        updates[key] = 0
      }
    })
    if (Object.keys(updates).length > 0) {
      form.setFieldsValue({ main: { ...form.getFieldValue("main"), ...updates } })
      handleChange()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripPrice?.id])

  const handleChange = () => {
    setTimeout(() => {
      const mainCounts = form.getFieldValue("main") || {}
      onChange?.(mainCounts)
      form.validateFields([["main", firstAllowedField], "guestTotal"]).catch(() => {})
    }, 0)
  }

  const validateGuestTotal = () => {
    const main = form.getFieldValue("main") || {}
    const total = Object.values(main).reduce((sum, v) => sum + (Number(v) || 0), 0)
    if (maxGuests != null && total > maxGuests) {
      return Promise.reject(new Error(t("booking.error_messages.max_guest_limit", { max: maxGuests })))
    }
    if (minGuests != null && total < minGuests) {
      return Promise.reject(new Error(t("booking.error_messages.min_guest_limit", { min: minGuests })))
    }
    return Promise.resolve()
  }

  return (
    <>
      <Space size="small" className={styles.inputGroup} wrap>
        {ALL_GUEST_FIELDS.map(({ key, labelKey, allowedFlag }) => {
          const disabled = tripPrice ? tripPrice[allowedFlag] === false : false
          const isFirstAllowed = key === firstAllowedField
          return (
            <Form.Item
              key={key}
              label={t(labelKey)}
              name={["main", key]}
              initialValue={isFirstAllowed ? defaultMinCount : 0}
              rules={
                !disabled && isFirstAllowed
                  ? [{ required: true, type: "number", min: 1, message: t("booking.error_messages.adults_required") }]
                  : []
              }
            >
              <InputNumber
                min={isFirstAllowed ? 1 : 0}
                className={styles.inputNumber}
                disabled={disabled}
                onChange={handleChange}
              />
            </Form.Item>
          )
        })}
      </Space>
      <Form.Item name="guestTotal" rules={[{ validator: validateGuestTotal }]} />
    </>
  )
}

GuestCountSection.propTypes = {
  form: PropTypes.object.isRequired,
  tripPrice: PropTypes.shape({
    adults_allowed: PropTypes.bool,
    children_allowed: PropTypes.bool,
    infants_allowed: PropTypes.bool,
    passengers_allowed: PropTypes.bool
  }),
  onChange: PropTypes.func,
  minGuests: PropTypes.number,
  maxGuests: PropTypes.number,
  bookingSelections: PropTypes.object
}

export default GuestCountSection
